/* eslint-disable camelcase */
import each from "lodash/each";
import groupBy from "lodash/groupBy";
import countBy from "lodash/countBy";
import isEmpty from "lodash/isEmpty";
import lodashMax from "lodash/max";
import lodashMin from "lodash/min";
import clone from "lodash/clone";
import reject from "lodash/reject";
import mapObject from "lodash/mapValues";
import isUndefined from "lodash/isUndefined";
import keys from "lodash/keys";
import filter from "lodash/filter";
import sortBy from "lodash/sortBy";
import SensorModel from "../models/sensorModel";
import FilterItemModel from "../models/filterItemModel";
import { cropr } from "../adapter";
import simplify from "simplify-js";
import { saveAs } from "./globalUtils";

const simplifyFactor = 0.75;
const csvDelimiter = "\t";
const csvEOL = "\r\n";

const updateFilterItemCount = (filterNames, results, filters, checkedFilters) => {
  each(filters, function (filtersItems, filterName) {
    var tempresults = results;

    var filtersToApply = reject(filterNames, function (fg) {
      return filterName === fg;
    });

    // Temp list of results filtered
    tempresults = applyFilters(filtersToApply, tempresults, filters, checkedFilters);
    const filterItemCounts = countBy(tempresults, function (result) {
      return result[filterName];
    });

    // Apply the filtercounts
    each(filtersItems, function (filterItem, key) {
      // Set count 0 if value is undefined
      const count = filterItemCounts[filterItem.title] ? filterItemCounts[filterItem.title] : 0;
      filterItem.count = count;
    });
  });
  return filters;
};

const applySearch = (search, results) => {
  if (search) {
    var searchLowerCase = search.toLowerCase();
    return filter(results, function (result) {
      if (result && result.name) {
        return result.name.toLowerCase().indexOf(searchLowerCase) !== -1;
      }
    });
  }
  return results;
};

const applySorter = (property, direction, results) => {
  results = sortBy(results, property);

  if (direction === "DESC") {
    results.reverse();
  }

  // Special treatment soilmoisture and refillCapacity filters
  // Moving the nodata values to the bottom despite the sorting
  if (
    property === "soilMoisture" ||
    property === "refillCapacity" ||
    property === "precipitationSumToday"
  ) {
    var noDataResults = [];
    each(results, function (result, key) {
      if (result.soilMoistureStatus === "noData") {
        noDataResults.push(results[key]);
        delete results[key]; // elements are null, not deleted
      }
    });
    results = results.concat(noDataResults);
  }

  return results.filter(function (el) {
    return el != null;
  });
};

const applyFilters = (filterNames, results, filters, checkedFilters) => {
  // Apply each filter
  let checkedIds = [];
  if (checkedFilters) {
    checkedIds = checkedFilters.split("-");
  }

  for (var i = 0; i < checkedIds.length; i++) checkedIds[i] = parseInt(checkedIds[i], 10);

  each(filterNames, function (filterName) {
    results = applyFilter(filterName, results, filters);
  });

  function applyFilter(filterName, results, filters) {
    // Find which filters are active within the group
    var activeFilterItems = filter(filters[filterName], function (filterItem) {
      return filterItem.reversed
        ? !checkedIds.includes(filterItem.id)
        : checkedIds.includes(filterItem.id);
    });

    // If any, filter the result results
    if (activeFilterItems.length > 0) {
      results = filter(results, function (result) {
        var valid = false;
        each(activeFilterItems, function (filter) {
          if (filter.title === result[filterName]) {
            valid = true;
          }

          // Also filter undefined values
          if (filter.title === "undefined") {
            if (isUndefined(result[filterName])) {
              valid = true;
            }
          }
        });
        return valid;
      });
    }
    return results;
  }
  return results;
};

const parseSensors = (sensors) => {
  sensors = sensors.map((sensor) => {
    let refillPoint = 0;

    let fieldCapacity = 0;
    if (sensor.configuration) {
      each(sensor.configuration.depths, function (depth, key) {
        if (depth.visible) {
          fieldCapacity = fieldCapacity + depth.scale;
        }
      });
    }

    // Rewrite the latest measurements
    var latestMeasurement = false;
    if (sensor.latest_measurement && sensor.configuration.data) {
      latestMeasurement = { timestamp: sensor.latest_measurement.timestamp };
      each(sensor.configuration.data.depths, function (values, depth) {
        if (sensor.latest_measurement.values.depths[depth]) {
          latestMeasurement[depth * 10] =
            sensor.latest_measurement.values.depths[depth].moisture_level;
        }
      });
    }

    var s = new SensorModel({
      id: sensor.id,
      product: sensor.product,
      name: sensor.configuration.name,
      owner: sensor.owner,
      role: sensor.role,
      latitude: sensor.latitude,
      longitude: sensor.longitude,
      type: sensor.sensor_type
        ? sensor.sensor_type.charAt(0).toUpperCase() + sensor.sensor_type.slice(1)
        : "",
      //        enabled: sensor.configuration.enabled,
      enabled: sensor.powered_on,
      active: sensor.has_recent_measurements,
      activePeriodStart: sensor.active_period_start,
      activePeriodEnd: sensor.active_period_end,
      fieldCapacity: fieldCapacity,
      refillPoint: refillPoint,
      warningPercentage: sensor.configuration.data.fc_warning_percentage,
      latestMeasurement: latestMeasurement,
      serialNumber: sensor.serial_number,
    });

    var depths = {};

    if (sensor.configuration.data) {
      // Get the depths
      each(sensor.configuration.data.depths, function (values, depth) {
        // Conversion to millimeters
        depth = depth * 10;
        depths[depth] = {
          fieldCapacity: parseFloat(values.field_capacity),
          originalFieldCapacity: parseFloat(values.field_capacity),
          refillPoint: parseFloat(values.refill_point),
          originalRefillPoint: parseFloat(values.refill_point),
          relativeFieldCapacity: values.scale,
          relativeRefillPoint: 0,
          scale: values.scale,
          originalScale: values.scale,
          visible: values.visible,
        };
      });
    }

    s.setDepths(depths);
    s.calculateSoilMoisture();

    s.advice = sensor.advice;
    s.precipitationSumList = sensor.precipitation_sum;
    return s;
  });

  // var groupFilter = groupBy(sensors, 'group');
  var typeFilter = groupBy(sensors, "type");
  var ownerFilter = groupBy(sensors, "owner");
  var soilMoistureStatusFilter = groupBy(sensors, "soilMoistureStatus");
  // var statusFilter = groupBy(sensors, 'status');
  var periodFilter = groupBy(sensors, "period");

  // Add empty filters
  if (!soilMoistureStatusFilter.belowRefillPoint) soilMoistureStatusFilter.belowRefillPoint = [];
  if (!soilMoistureStatusFilter.belowWarningPoint) soilMoistureStatusFilter.belowWarningPoint = [];
  if (!soilMoistureStatusFilter.aboveFieldCapacity)
    soilMoistureStatusFilter.aboveFieldCapacity = [];
  if (!soilMoistureStatusFilter.normal) soilMoistureStatusFilter.normal = [];
  if (!soilMoistureStatusFilter.noData) soilMoistureStatusFilter.noData = [];

  if (!periodFilter.last_year) periodFilter.last_year = [];
  if (!periodFilter.past) periodFilter.past = [];
  if (!periodFilter.now) periodFilter.now = [];
  if (!periodFilter.none) periodFilter.none = [];

  var filterIdCount = 0;

  var filters = {
    type: mapObject(typeFilter, function (filterItem, title) {
      filterIdCount++;
      return new FilterItemModel(filterIdCount, title, "type", filterItem.length, false);
    }),

    owner: mapObject(ownerFilter, function (filterItem, title) {
      filterIdCount++;
      return new FilterItemModel(filterIdCount, title, "owner", filterItem.length, false);
    }),

    soilMoistureStatus: mapObject(soilMoistureStatusFilter, function (filterItem, title) {
      var id = 100;
      if (title === "belowRefillPoint") {
        id = 101;
      }
      if (title === "belowWarningPoint") {
        id = 102;
      }
      if (title === "aboveFieldCapacity") {
        id = 103;
      }
      if (title === "normal") {
        id = 104;
      }
      if (title === "noData") {
        id = 105;
      }

      return new FilterItemModel(id, title, "soilMoistureStatus", filterItem.length, false);
    }),

    period: mapObject(periodFilter, function (filterItem, title) {
      filterIdCount++;
      return new FilterItemModel(
        filterIdCount,
        title,
        "period",
        filterItem.length,
        title === "now"
      );
    }),
  };

  // Sort and order the filters
  filters.group = sortBy(filters.group, "title");
  filters.type = sortBy(filters.type, "title");
  filters.owner = sortBy(filters.owner, "title");

  // Only show the new filter if there are new sensors
  var nonePeriodSensors;
  if (filters.period.none.count > 0) {
    nonePeriodSensors = filters.period.none;
  }

  filters.period = [filters.period.now, filters.period.last_year, filters.period.past];

  if (nonePeriodSensors) {
    filters.period.push(nonePeriodSensors);
  }

  filters.soilMoistureStatus = [
    filters.soilMoistureStatus.belowRefillPoint,
    filters.soilMoistureStatus.belowWarningPoint,
    filters.soilMoistureStatus.aboveFieldCapacity,
    filters.soilMoistureStatus.normal,
    filters.soilMoistureStatus.noData,
  ];

  sensors = sortBy(sensors, "name");

  return { sensors, filters };
};

// Prepare data for submission
const prepareData = (sensor) => {
  var depths = {};

  each(sensor.depths, function (values, depth) {
    depths[depth / 10] = {
      field_capacity: parseFloat(values.fieldCapacity), // 88
      refill_point: parseFloat(values.refillPoint), // 65

      // scale hier veranderen van inches naar mm???

      //        scale: parseInt(values.scale),
      scale: parseInt(values.scale * 10) / 10,
      visible: values.visible,
    };
  });

  // Sensor uitzetten
  if (sensor.enabled === true && sensor.turned_off === true) {
    sensor.enabled = false;
  }

  // Configuration
  var configuration = {
    depths: depths,
    enabled: sensor.enabled,
    show_precipitation: sensor.showPrecipitation,
    show_temperature: sensor.showTemperature,
    fc_warning_percentage: sensor.warningPercentage,
    soil_type: sensor.soilType,
  };

  // The end date could be null
  var endDate = null;
  if (sensor.endDate) {
    endDate = sensor.endDate.format("YYYY-MM-DD");
  }

  return {
    view_period_end: endDate, // Sat, 01 Jan 2000 00:15:00 GMT
    view_period_start: sensor.startDate.format("YYYY-MM-DD"),
    data: configuration,
    id: sensor.id,
    installation: sensor.installation,
    latitude: parseFloat(sensor.latitude),
    longitude: parseFloat(sensor.longitude),
    name: sensor.name,
  };
};

const parseSensor = (sensor, advice = false, domainDays = 14) => {
  if (!sensor) return sensor;
  var refillPoint = 0;
  var fieldCapacity = 0;
  each(sensor.configuration.data.depths, function (depth, key) {
    if (sensor.configuration.data.depths[key].visible) {
      // fieldCapacity = fieldCapacity + depth.field_capacity;
      fieldCapacity = fieldCapacity + depth.scale;
    }
  });

  // Rewrite the latest measurements
  var latestMeasurement = false;
  if (sensor.latest_measurement) {
    latestMeasurement = { timestamp: sensor.latest_measurement.timestamp };
    each(sensor.configuration.data.depths, function (values, depth) {
      latestMeasurement[depth * 10] = sensor.latest_measurement.values.depths[depth].moisture_level;
    });
  }

  var cd_props = sensor.latest_cd_properties;

  var sensorModel = new SensorModel({
    id: sensor.id,
    product: sensor.product,
    //      id: sensor.configuration.id,
    name: sensor.configuration.name,
    owner: sensor.owner,
    installation: sensor.configuration.installation,
    role: sensor.role,
    latitude: sensor.latitude,
    longitude: sensor.longitude,
    type: sensor.sensor_type
      ? sensor.sensor_type.charAt(0).toUpperCase() + sensor.sensor_type.slice(1)
      : "",
    //     enabled: sensor.configuration.enabled,
    enabled: sensor.powered_on,
    active: sensor.has_recent_measurements,
    soilType: sensor.configuration.data.soil_type,
    activePeriodStart: sensor.active_period_start,
    activePeriodEnd: sensor.active_period_end,
    showPrecipitation: sensor.configuration.data.show_precipitation,
    showTemperature: sensor.configuration.data.show_temperature,
    warningPercentage: sensor.configuration.data.fc_warning_percentage,
    csq: cd_props && cd_props.values ? cd_props.values.csq : "",
    serialNumber: sensor.serial_number,
    voltage: cd_props && cd_props.values ? cd_props.values.voltage : "",
    battery: sensor.battery,
    rssi: sensor.rssi,
    // soilMoisture: soilMoisture,
    fieldCapacity: fieldCapacity,
    refillPoint: refillPoint,
    // refillCapacity: refillCapacity,
    latestMeasurement: latestMeasurement,
    viewSettingsId: sensor.configuration.id,
    sensors: sensor.sensors,
    advice_region: sensor.advice ? sensor.advice_region : "",
    turned_off: false,
  });

  var depths = {};
  var moisture = {};
  var temperature = {};
  var precipitation = [];

  // Allvalues for scales, should start with 0 because of the refillpoint
  var allValues = [0];
  var allTemperatureValues = [];

  // A single temp measurement for all sensors
  var singleTemperatureSensor = [];

  // Get the depths
  each(sensor.configuration.data.depths, function (values, depth) {
    // Conversion to millimeters
    depth = depth * 10;

    // Genereate a new depth
    depths[depth] = {
      fieldCapacity: values.field_capacity ? Number(values.field_capacity) : 1 + values.scale,
      originalFieldCapacity: values.field_capacity
        ? Number(values.field_capacity)
        : 1 + values.scale,
      refillPoint: values.refill_point ? Number(values.refill_point) : 1,
      originalRefillPoint: values.refill_point ? Number(values.refill_point) : 1,
      relativeFieldCapacity: values.scale,
      relativeRefillPoint: 0,
      scale: values.scale,
      originalScale: values.scale,
      scalePerUnit: (Number(values.field_capacity) - Number(values.refill_point)) / values.scale,
      visible: values.visible,
    };

    // Set empty measurement depths
    moisture[depth] = [];
    temperature[depth] = [];
  });

  sensorModel.setDepths(depths);

  // Calculate the maximum and minimum measurement values, taking in account the configuration
  each(sensor.measurements, function (measurement, key) {
    each(sensor.configuration.data.depths, function (single_depth_val, depth) {
      // take the depth from config,
      allValues.push(measurement.values.depths[depth].moisture_level); // and not from measurement.values
      if (measurement.values.depths[depth].soil_temperature) {
        allTemperatureValues.push(measurement.values.depths[depth].soil_temperature);
      }
    });

    if (measurement.values.temperature) {
      allTemperatureValues.push(measurement.values.temperature);
    }
  });

  // @TODO: calc temperature the same way

  var max = lodashMax(allValues);
  var min = lodashMin(allValues);
  var maxTemperature = lodashMax(allTemperatureValues);
  var minTemperature = lodashMin(allTemperatureValues);

  var chartScale = 100 / (max - min);
  var chartScaleTemperature = 100 / (maxTemperature - minTemperature);

  // Calculate the timezone offset
  var d = new Date();
  var timezoneOffset = d.getTimezoneOffset() * 60000;

  // Set the measurements
  each(sensor.measurements, function (measurement, key) {
    var timestamp = measurement.timestamp * 1000 + timezoneOffset;
    var date = new Date(timestamp);

    each(sensor.configuration.data.depths, function (single_depth_val, depth) {
      // take the depth from config,
      // and not from measurement.values
      moisture[depth * 10].push({
        x: timestamp,
        y: measurement.values.depths[depth].moisture_level * chartScale,
        timestamp: date,
        // active: true,
      });

      if (measurement.values.depths[depth].soil_temperature) {
        temperature[depth * 10].push({
          x: timestamp,
          y: measurement.values.depths[depth].soil_temperature * chartScaleTemperature,
          timestamp: date,
          // active: true,
        });
      }
    });

    // Add rain to the measurements
    precipitation.push({
      timestamp: date,
      y: measurement.values.precipitation ? measurement.values.precipitation : 0,
      // active: true,
    });

    if (measurement.values.temperature) {
      singleTemperatureSensor.push({
        x: timestamp,
        y: measurement.values.temperature * chartScaleTemperature,
        timestamp: date,
        // active: true,
      });
    }
  });

  // The last data point we received
  var lastDataPoint = false;
  sensorModel.measurements.moisture = moisture;
  if (!isEmpty(sensorModel.measurements.moisture)) {
    depths = keys(sensorModel.depths);
    lastDataPoint =
      sensorModel.measurements.moisture[lodashMin(depths)][
        sensorModel.measurements.moisture[lodashMin(depths)].length - 1
      ];
  }

  sensorModel.allmeasurements = sensor.measurements;
  if (sensor.advice != null) {
    sensorModel.allmeasurements = sensorModel.allmeasurements.concat(sensor.advice);
  }

  if (advice) {
    // Add the advices
    each(sensor.advice, function (measurement, key) {
      var timestamp = measurement.timestamp * 1000 + timezoneOffset;
      var date = new Date(timestamp);
      each(sensor.configuration.data.depths, function (single_depth_val, depth) {
        // take the depth from config,
        // and not from measurement.values
        if (measurement.values.depths[depth]) {
          moisture[depth * 10].push({
            x: timestamp,
            y: measurement.values.depths[depth].moisture_level * chartScale,
            timestamp: date,
            // active: true,
          });

          if (measurement.values.depths[depth].soil_temperature) {
            temperature[depth * 10].push({
              x: timestamp,
              y: measurement.values.depths[depth].soil_temperature * chartScaleTemperature,
              timestamp: date,
              // active: true,
            });
          }
        }
      });

      // Add rain to the measurements
      precipitation.push({
        timestamp: date,
        y: measurement.values.precipitation ? measurement.values.precipitation : 0,
        // active: true,
      });

      if (measurement.values.temperature) {
        singleTemperatureSensor.push({
          x: timestamp,
          y: measurement.values.temperature * chartScaleTemperature,
          timestamp: date,
          // active: true,
        });
      }
    });
  }

  sensorModel.calculateSoilMoisture();

  // @TODO make setfunctions in the sensormodel
  sensorModel.measurements.precipitation = precipitation;
  sensorModel.measurements.moisture = moisture;
  sensorModel.measurements.temperature = temperature;
  // Compress temperature depending on type of temerature we have (single or multiple)
  if (singleTemperatureSensor.length > 0) {
    singleTemperatureSensor = simplify(singleTemperatureSensor, simplifyFactor * 1.5, true); // 1

    // Remove the scale
    singleTemperatureSensor = singleTemperatureSensor.map(function (measurement) {
      return { timestamp: measurement.timestamp, y: measurement.y / chartScaleTemperature };
    });

    each(sensorModel.depths, function (value, depth) {
      sensorModel.measurements.temperature[depth] = singleTemperatureSensor;
    });
  } else {
    sensorModel.measurements.temperature = mapObject(
      sensorModel.measurements.temperature,
      function (values) {
        var simplifiedMeasurements = simplify(values, simplifyFactor * 1.5, true); // 1
        return simplifiedMeasurements.map(function (measurement) {
          return { timestamp: measurement.timestamp, y: measurement.y / chartScaleTemperature };
        });
      }
    );
  }

  calculateMeasurements(chartScale, sensorModel);

  var firstDataPointTimestamp = false;
  var lastDataPointTimestamp = false;

  const domain = [];

  if (sensor.measurements[0]) {
    firstDataPointTimestamp = sensor.measurements[0].timestamp;
    lastDataPointTimestamp = sensor.measurements[sensor.measurements.length - 1].timestamp;
  }

  if (sensor.advice && sensor.advice.length > 0) {
    lastDataPointTimestamp = sensor.advice[sensor.advice.length - 1].timestamp;
  }

  if (lastDataPointTimestamp && firstDataPointTimestamp) {
    var firstDataPoint = new Date(
      Math.max(
        firstDataPointTimestamp * 1000 + timezoneOffset,
        // @TODO argument this.domainDays
        (lastDataPointTimestamp - 60 * 60 * 24 * domainDays) * 1000
      )
    );
    var lastDataPoint2 = new Date(lastDataPointTimestamp * 1000 + timezoneOffset);

    // The start date should be the last measurement minus 14 days OR the earliest measurement if smaller
    domain.push(firstDataPoint);
    domain.push(lastDataPoint2);
  }

  return { sensor: sensorModel, lastDataPoint, defaultDomain: domain };
};

const calculateMeasurements = (chartScale, sensor) => {
  var cumulativeMoisture = {};

  // Cumulative active depth available soil moisture measurements
  each(sensor.depths, function (values, depth) {
    // Don't use hidden depths
    if (!values.visible) {
      return;
    }

    // Init the array and copy the values
    cumulativeMoisture[depth] = [];

    each(sensor.measurements.moisture[depth], function (value, key) {
      var newValue = clone(value);

      // Cut the cumulative value at zero
      newValue.y = Math.max(0, newValue.y);
      cumulativeMoisture[depth][key] = newValue;
    });

    // Add other values
    each(sensor.depths, function (values, otherDepth) {
      if (otherDepth > depth && values.visible) {
        // Loop trough al the depth measurements and add to the other
        each(cumulativeMoisture[depth], function (value, key) {
          cumulativeMoisture[depth][key].y =
            value.y + Math.max(0, sensor.measurements.moisture[otherDepth][key].y);
        });
      }
    });
  });

  sensor.measurements.moisture = mapObject(sensor.measurements.moisture, function (values) {
    var simplifiedMeasurements;
    if (values.length > 2000) {
      simplifiedMeasurements = simplify(values, simplifyFactor, true);
    } else {
      simplifiedMeasurements = values;
    }
    return simplifiedMeasurements.map(function (measurement) {
      return {
        timestamp: measurement.timestamp,
        y: measurement.y / chartScale,
      };
    });
  });

  sensor.measurements.cumulativeMoisture = mapObject(cumulativeMoisture, function (values) {
    var simplifiedMeasurements;
    if (values.length > 2000) {
      simplifiedMeasurements = simplify(values, simplifyFactor * 0.5, true);
    } else {
      simplifiedMeasurements = values;
    }
    return simplifiedMeasurements.map(function (measurement) {
      return {
        timestamp: measurement.timestamp,
        y: measurement.y / chartScale,
      };
    });
  });

  sensor.measurements.lowResCumulativeMoisture = mapObject(cumulativeMoisture, function (values) {
    var simplifiedMeasurements;
    if (values.length > 2000) {
      simplifiedMeasurements = simplify(values, simplifyFactor, true);
    } else {
      simplifiedMeasurements = values;
    }
    return simplifiedMeasurements.map(function (measurement) {
      return {
        timestamp: measurement.timestamp,
        y: measurement.y / chartScale,
      };
    });
  });
};

const export2Csv = (sensor, domain) => {
  var tableheader = ["date", "time"];
  var unitheader = ["", ""];
  var csvfilename = "dacom_soil_moisture_data_" + sensor.serialNumber + ".csv";
  var d = new Date();
  var offset = d.getTimezoneOffset() * 1000 * 60;

  // order matters, first moisture_level, then soil temp, then precipitation
  each(sensor.sensors, function (sensor_type, key) {
    if (sensor_type === "moisture_level") {
      each(sensor.depths, function (value, key) {
        tableheader.push(sensor_type + "_" + key / 10);
        unitheader.push(cropr.l10n.unit_precipitation_display);
      });
    }
  });

  each(sensor.sensors, function (sensor_type, key) {
    if (sensor_type === "soil_temperature") {
      each(sensor.depths, function (value, key) {
        tableheader.push(sensor_type + "_" + key / 10);
        unitheader.push(cropr.l10n.unit_temp_display);
      });
    }
  });

  each(sensor.sensors, function (sensor_type, key) {
    if (sensor_type === "precipitation") {
      tableheader.push(sensor_type);
      unitheader.push(cropr.l10n.unit_precipitation_display);
    }
  });

  var selectionStartDate = domain[0];
  var selectionEndDate = domain[1];

  var ts = new Date(selectionStartDate.getTime());
  ts.setMinutes(0);
  ts.setSeconds(0);
  ts.setMilliseconds(0);

  var timestamps_arr = [];
  while (ts < selectionEndDate) {
    timestamps_arr.push(ts.setHours(ts.getHours() + 1)); // de timestamp
  }

  var valuescsv = "";
  each(tableheader, function (th_val, th_key) {
    valuescsv += th_val + csvDelimiter;
  });
  valuescsv += csvEOL;

  each(unitheader, function (th_val, th_key) {
    valuescsv += th_val + csvDelimiter;
  });
  valuescsv += csvEOL;

  each(sensor.allmeasurements, function (measurement, key) {
    ts = new Date(measurement.timestamp * 1000 + offset);

    var idx = timestamps_arr.indexOf(ts.getTime());
    if (idx > -1) {
      // datum-tijd
      valuescsv += ts.toLocaleDateString().replace(/\u200e/g, "") + csvDelimiter; // the unicode 200e sign is an IE bug, so remove those characters.
      valuescsv += ts.toLocaleTimeString().replace(/\u200e/g, "") + csvDelimiter;

      //soil_moisture
      function checkMetaContainsSoilMoisture(meta_key) {
        return meta_key === "moisture_level";
      }

      if (sensor.sensors.findIndex(checkMetaContainsSoilMoisture) >= 0) {
        each(measurement.values.depths, function (depth_values, depth_key) {
          if (Number.isFinite(depth_values.moisture_level)) {
            mval = cropr.l10n_utils.mm_to_l10n(depth_values.moisture_level);
            valuescsv +=
              mval.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + csvDelimiter;
          } else {
            valuescsv += csvDelimiter;
          }
        });
      }

      // soil_temperature
      function checkMetaContainsSoilTemp(meta_key) {
        return meta_key === "soil_temperature";
      }

      if (sensor.sensors.findIndex(checkMetaContainsSoilTemp) >= 0) {
        each(measurement.values.depths, function (depth_values, depth_key) {
          if (Number.isFinite(depth_values.soil_temperature)) {
            mval = cropr.l10n_utils.celsius_to_l10n(depth_values.soil_temperature);
            valuescsv +=
              mval.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + csvDelimiter;
          } else {
            valuescsv += csvDelimiter;
          }
        });
      }

      //precipitation
      function checkMetaContainsPercipitation(meta_key) {
        return meta_key === "precipitation";
      }

      if (sensor.sensors.findIndex(checkMetaContainsPercipitation) >= 0) {
        if (Number.isFinite(measurement.values.precipitation)) {
          var mval = cropr.l10n_utils.mm_to_l10n(measurement.values.precipitation);
          valuescsv +=
            mval.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + csvDelimiter;
        } else {
          valuescsv += csvDelimiter;
        }
      }

      valuescsv += csvEOL;
    }
  });

  saveAs(valuescsv, csvfilename);
};

export {
  parseSensors,
  applySearch,
  applyFilters,
  applySorter,
  updateFilterItemCount,
  parseSensor,
  export2Csv,
  prepareData,
};
